var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Paper, Stack, Typography, styled } from '@mui/material';
import { keyBy } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { StatusPermission } from '../../enum';
import { RumEntityLabel } from '../../observability/rumEntity';
import { ErrorMessageBox, ExitBar, FallbackComponent } from '../common';
import { PageBody, SubTitle } from '../layout/styled';
import { useReportAPI, useReportEffectivePermission } from '../report/selectors';
import { PLAN_COLORS } from '../wizard/constants';
import { PlanCurrencyConversion } from '../wizard/financial';
import { AddSingleSkill } from './addSingleSkills';
import { AttentionDialog } from './addSingleSkills/attentionDialog';
import { MultiSkillLoader } from './loader';
import { MultiSkillGroup } from './multiskillGroup';
import { commonFootprint, selectIsValidGroup, useGetCopiedGroups, useMultiSkillAPI, useMultiSkillGroups, useMultiSkillParams, useMultiskillFxRatesAPI, } from './selectors';
import { MultiSkillStep } from './types';
var BasePanel = styled(Paper)(function (_a) {
    var theme = _a.theme;
    return ({
        position: 'relative',
        padding: theme.spacing(8),
        borderRadius: theme.shape.borderRadius,
        height: '100%',
        overflow: 'auto',
    });
});
var ComponentByStep = (_a = {},
    _a[MultiSkillStep.Add] = AddSingleSkill,
    _a[MultiSkillStep.Input] = MultiSkillGroup,
    _a);
export var MultiSkill = function () {
    var _a, _b, _c, _d;
    var t = useTranslation(['multiSkill', 'translation']).t;
    var step = useMultiSkillParams().step;
    var reportPlanData = useReportAPI()[0];
    var multiSkillData = useMultiSkillAPI()[0];
    var _e = useSearchParams(), searchParams = _e[0], setSearchParams = _e[1];
    var _f = useState([]), multiSkillGroups = _f[0], setMultiSkillGroups = _f[1];
    var _g = useMultiskillFxRatesAPI(), ratesData = _g[0], fetchRates = _g[1];
    var groupCurrenciesRef = useRef([]);
    var scenario = (_a = reportPlanData.data) === null || _a === void 0 ? void 0 : _a.scenarios[0];
    var multiSkillGroupData = useMultiSkillGroups();
    var copyMultiSkillGroups = useGetCopiedGroups();
    var _h = useState(false), isInvalidGroup = _h[0], setIsInvalidGroup = _h[1];
    var Component = ComponentByStep[step];
    var capacityPermission = useReportEffectivePermission();
    var isPlanLocked = !!((scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline) && (scenario === null || scenario === void 0 ? void 0 : scenario.isLocked));
    var lastSubmittedKeyRef = useRef('');
    var handleFetchRates = useCallback(function (currencies) {
        var isExplicitCall = !Array.isArray(currencies);
        if (Array.isArray(currencies)) {
            groupCurrenciesRef.current = currencies;
        }
        var codes = Array.from(new Set(groupCurrenciesRef.current));
        var key = codes
            .slice()
            .sort(function (a, b) { return a.localeCompare(b); })
            .join(',');
        if (!isExplicitCall && key === lastSubmittedKeyRef.current)
            return;
        lastSubmittedKeyRef.current = key;
        fetchRates(codes);
    }, [fetchRates]);
    useEffect(function () {
        if (!multiSkillGroupData.length && copyMultiSkillGroups.length) {
            var memberMap_1 = keyBy(multiSkillData.data, 'id');
            var updatedGroups = copyMultiSkillGroups
                .map(function (group) {
                var validMatch = selectIsValidGroup(group.children)(multiSkillData.data);
                if (!validMatch)
                    return null;
                var members = group.children.map(function (childId) { return memberMap_1[childId]; });
                if (members.some(function (member) { return !member; }))
                    return null;
                return commonFootprint(members).valid ? group : null;
            })
                .filter(Boolean);
            setMultiSkillGroups(updatedGroups);
            setIsInvalidGroup(copyMultiSkillGroups.length !== updatedGroups.length);
        }
        else {
            setMultiSkillGroups(multiSkillGroupData);
        }
    }, [multiSkillData.data, copyMultiSkillGroups, multiSkillGroupData]);
    var goToStep = useCallback(function (s) {
        searchParams.set('step', "".concat(s));
        setSearchParams(searchParams);
    }, [searchParams, setSearchParams]);
    return (_jsxs(_Fragment, { children: [_jsx(MultiSkillLoader, {}), _jsxs(FallbackComponent, __assign({ requesting: !!multiSkillData.requesting || !!reportPlanData.requesting }, { children: [_jsx(ErrorMessageBox, { errors: [reportPlanData.error, multiSkillData.error], sx: { margin: 3, marginBottom: 0 } }), _jsx(ExitBar, { title: (_b = reportPlanData.data) === null || _b === void 0 ? void 0 : _b.name, titleEntity: { kind: 'plan', id: (_c = reportPlanData.data) === null || _c === void 0 ? void 0 : _c.id } }), !reportPlanData.error && (_jsxs(PageBody, { children: [_jsxs(Stack, __assign({ direction: "column", spacing: 4 }, { children: [_jsx(Typography, __assign({ variant: "h1" }, { children: t('title') })), _jsxs(Stack, __assign({ direction: "row", justifyContent: "space-between", alignItems: "center" }, { children: [_jsx(SubTitle, { children: t('description') }), step === MultiSkillStep.Input && (_jsx(PlanCurrencyConversion, { ratesData: ratesData, fetchRates: handleFetchRates }))] }))] })), _jsxs(BasePanel, { children: [_jsx(Stack, __assign({ bgcolor: PLAN_COLORS[(_d = scenario === null || scenario === void 0 ? void 0 : scenario.colorIndex) !== null && _d !== void 0 ? _d : 0][0], color: "common.white", padding: 4, borderRadius: 1, direction: "row", alignItems: "center", mb: 5 }, { children: _jsx(Typography, __assign({ variant: "subtitle1" }, { children: (scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline) ? (t('translation:baseline')) : (_jsx(RumEntityLabel, __assign({ kind: "scenario", id: scenario === null || scenario === void 0 ? void 0 : scenario.id }, { children: scenario === null || scenario === void 0 ? void 0 : scenario.name }))) })) })), _jsx(Component, { multiSkillGroups: multiSkillGroups, setMultiSkillGroups: setMultiSkillGroups, goToStep: goToStep, permission: capacityPermission === StatusPermission.Edit && !isPlanLocked, onCurrenciesChange: handleFetchRates }), _jsx(AttentionDialog, { isInvalidGroup: isInvalidGroup })] })] }))] }))] }));
};
